import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Page, PageHeader, Section } from '../../../components/layout/primitives';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import Modal from '../../../components/ui/Modal';
import { getUser } from '../../../lib/storage';
import { agentCategoryService } from '../../../services/agentCategoryService';
import { UserRoleEnums } from '../../../utility/userRoles';
import { Checkbox, TextArea } from '../../../components/ui/Field';
import { Table } from '../../../components/ui/Table';

type Category = {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  createdAt?: string;
};

const CategoryPage: React.FC = () => {
  const user = getUser();
  const role = user?.user?.role;

  const [items, setItems] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res: any = await agentCategoryService.list({ limit: 100, search });
      const rows = (res?.agentCategories || res?.data?.agentCategories || []) as Category[];
      setItems(rows);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setIsActive(true);
    setOpenModal(true);
  };

  const openEdit = (item: Category) => {
    setEditing(item);
    setName(item.name || '');
    setDescription(item.description || '');
    setIsActive(!!item.isActive);
    setOpenModal(true);
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        isActive,
      };

      if (editing?.id) {
        await agentCategoryService.update(editing.id, payload);
        toast.success('Category updated');
      } else {
        await agentCategoryService.create(payload);
        toast.success('Category created');
      }

      setOpenModal(false);
      setEditing(null);
      await load();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await agentCategoryService.remove(id);
      toast.success('Category deleted');
      await load();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to delete category');
    }
  };

  const filtered = useMemo(() => items, [items]);

  if (role !== UserRoleEnums.SUPER_ADMIN) {
    return (
      <Page contentClassName="py-8">
        <Card className="max-w-4xl">
          <h1 className="text-xl font-semibold">Unauthorized</h1>
          <p className="mt-1 text-agentic-text-secondary">This screen is available only to Super Admin users.</p>
        </Card>
      </Page>
    );
  }

  return (
    <Page contentClassName="py-6">
      <PageHeader
        className="py-0"
        title="Category Management"
        subtitle="Create and manage agent categories."
        aside={<Button onClick={openCreate}>+ New Category</Button>}
      />

      <Section className="py-6">
        <Card className="mb-6">
          <Input
            type="text"
            placeholder="Search categories by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="md:max-w-md"
          />
        </Card>

        {loading ? (
          <div className="py-12 text-center text-agentic-text-secondary">Loading categories...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-agentic-text-secondary">No categories found.</div>
        ) : (
          <Card className="overflow-x-auto p-0">
            <Table className="w-full min-w-[760px]">
              <thead className="bg-agentic-gray-100 text-left text-sm text-agentic-text-secondary">
                <tr>
                  <th className="p-4">Name</th>
                  <th className="p-4">Description</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Created</th>
                  <th className="p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="border-t border-agentic-gray-200 hover:bg-agentic-gray-50">
                    <td className="p-4 font-medium text-agentic-text-primary">{item.name}</td>
                    <td className="p-4 text-agentic-text-secondary">{item.description || '-'}</td>
                    <td className="p-4">
                      <span className={`rounded px-2 py-1 text-xs ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-agentic-text-secondary">{item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Button onClick={() => openEdit(item)} size="sm" variant="secondary">Edit</Button>
                        <Button onClick={() => onDelete(item.id)} size="sm" variant="danger">Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
      </Section>

      <Modal
        isOpen={openModal}
        onClose={() => !saving && setOpenModal(false)}
        title={editing ? 'Edit Category' : 'Create Category'}
        size="md"
      >
        <form onSubmit={onSave} className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} label="Name *" placeholder="e.g., Healthcare" />
          <div>
            <label className="mb-2 block text-sm font-medium text-agentic-text-primary">Description</label>
            <TextArea tone="agentic"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-xl border px-4 py-3"
              rows={4}
              placeholder="Optional description"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-agentic-text-primary">
            <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpenModal(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" loading={saving} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Category'}</Button>
          </div>
        </form>
      </Modal>
    </Page>
  );
};

export default CategoryPage;
